import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { PortalAuthService } from 'src/app/core/auth/portal-auth.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { IonicModule } from '@ionic/angular';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { TranslatePipe } from '@ngx-translate/core';

// Loop-guard (docs/KONZEPT-Default-Tenant.md): the auto-apply-on-login redirect fires at
// most once per browser session, even if the default turns out to be stale/inaccessible
// and the user bounces back here. NOTE: this literal is duplicated in
// PortalAuthService.logout() (src/app/core/auth/portal-auth.service.ts), which clears it
// on logout so the guard re-arms for the next login in the same tab. Keep both in sync.
const DEFAULT_APPLIED_KEY = 'defaultApplied';

@Component({
    selector: 'app-no-tenant',
    templateUrl: 'no-tenant.page.html',
    styleUrls: ['no-tenant.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, TooltipDirective, TranslatePipe]
})
export class NoTenantPage implements OnInit {
  /** True while auto-redirecting into the user's default tenant. Hides the static card. */
  isRedirecting = false;
  /**
   * True until it is clear whether there is an organization to sign in with. The card ("no company linked")
   * only appears after that: showing it first made everyone who is about to be sent on see an error flash by.
   */
  isChecking = true;
  /** The organizations could not be read: not the same as "no company linked", so it gets its own card with a retry. */
  loadError = false;

  constructor(
    public portalAuth: PortalAuthService,
    private userApi: UserApiService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadError = false;
    this.isChecking = true;
    if (sessionStorage.getItem(DEFAULT_APPLIED_KEY)) {
      this.finishChecking();
      return;
    }

    this.userApi.getOrganizations().subscribe({
      next: orgs => {
        // Exactly one org: no ambiguity, always auto-switch — a single-tenant user has no
        // reason to see this picker at all, regardless of whether a default is set (Auth0
        // doesn't always resolve a lone org on a plain login without an explicit org_id,
        // which is why this page can be reached in the first place).
        // Multiple orgs: only auto-switch to an explicit, still-valid default; otherwise
        // fall back to the static card below (the header avatar's org switcher, rendered
        // on this route too, is still reachable from there).
        const defaultOrgId = this.portalAuth.getDefaultOrgId();
        const target = orgs.length === 1
          ? orgs[0].orgId
          : orgs.find(o => o.orgId === defaultOrgId)?.orgId ?? null;

        if (!target) {
          this.finishChecking();
          return;
        }

        sessionStorage.setItem(DEFAULT_APPLIED_KEY, '1');
        this.isRedirecting = true;
        this.isChecking = false;
        this.cdr.markForCheck();
        this.portalAuth.loginWithOrg(target);
      },
      // Organizations failed to load: say so (with a retry) rather than claiming that no company is linked.
      error: () => {
        this.loadError = true;
        this.finishChecking();
      },
    });
  }

  private finishChecking(): void {
    this.isChecking = false;
    this.cdr.markForCheck();
  }

  logout(): void {
    this.portalAuth.logout();
  }
}
